if (!customElements.get('product-shipping-estimate')) {
  // Shared with Mix Pallets so a country picked there is reused on PDP.
  const DESTINATION_STORAGE_KEY = 'mix-pallets-destination';
  const STATE_STORAGE_KEY = 'product-shipping-estimate-state';
  const DEFAULT_API_URL = 'https://api.staging.tryaladdin.net/pricing/v1/estimate/by-destination';
  const FETCH_DEBOUNCE_MS = 300;

  // Mirrors mix-pallets-shipping-estimate / checkout rate-presentation.
  const PRESENTED_CODES = [
    'ISTANBUL_PICKUP',
    'US_ADDRESS_SEA',
    'US_ADDRESS_AIR',
    'NON_US_AIR',
    'EXPRESS_DELIVERY'
  ];

  const FALLBACK_NAMES = {
    ISTANBUL_PICKUP: 'Pickup at Istanbul',
    US_ADDRESS_SEA: 'Econom',
    US_ADDRESS_AIR: 'Standart',
    NON_US_AIR: 'International',
    EXPRESS_DELIVERY: 'Express Delivery'
  };

  const COUNTRY_CODES = ('AD,AE,AF,AG,AI,AL,AM,AO,AR,AS,AT,AU,AW,AX,AZ,BA,BB,BD,BE,BF,BG,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,'
    + 'BS,BT,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CU,CV,CW,CX,CY,CZ,DE,DJ,DK,DM,DO,DZ,EC,EE,EG,EH,ER,ES,ET,'
    + 'FI,FJ,FK,FM,FO,FR,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GT,GU,GW,GY,HK,HN,HR,HT,HU,ID,IE,IL,IM,IN,IO,IQ,IR,'
    + 'IS,IT,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KP,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MH,MK,ML,'
    + 'MM,MN,MO,MP,MQ,MR,MS,MT,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NL,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PR,'
    + 'PS,PT,PW,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SI,SJ,SK,SL,SM,SN,SO,SR,SS,ST,SV,SX,SY,SZ,TC,TD,TG,TH,TJ,TK,'
    + 'TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,US,UY,UZ,VA,VC,VE,VG,VI,VN,VU,WF,WS,YE,YT,ZA,ZM,ZW').split(',');

  const POPULAR_COUNTRIES = ['US', 'TR', 'CA', 'GB', 'AU', 'JP', 'KR', 'SA', 'DK'];

  class ProductShippingEstimate extends HTMLElement {
    connectedCallback() {
      this.i18n = this.parseJson(this.dataset.i18n, {});
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.apiUrl = this.dataset.apiUrl || DEFAULT_API_URL;
      this.autodetectCountry = this.dataset.autodetectCountry !== 'false';
      this.detectedCountry = String(this.dataset.detectedCountry || '').trim().toUpperCase();
      this.variants = this.parseJson(this.querySelector('[data-pse-variants]')?.textContent, []);
      this.currentVariantId = Number.parseInt(this.dataset.currentVariant, 10) || this.variants[0]?.id;

      this.destination = this.readStoredDestination();
      this.scenarios = [];
      this.scenarioId = null;
      this.isLoading = false;
      this.hasError = false;
      this.fetchTimer = null;
      this.fetchAbort = null;
      this.lastFetchedKey = '';

      this.trigger = this.querySelector('[data-pse-trigger]');
      this.pop = this.querySelector('[data-pse-pop]');
      this.listEl = this.querySelector('[data-pse-list]');
      this.statusEl = this.querySelector('[data-pse-status]');
      this.ctxEl = this.querySelector('[data-pse-ctx]');
      this.shipToLabelEl = this.querySelector('[data-pse-shipto-label]');
      this.picked = this.querySelector('[data-pse-picked]');
      this.destinationOverlay = this.querySelector('[data-pse-destination]');

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.trigger?.addEventListener('click', () => this.openFlow(), { signal });
      this.querySelector('[data-pse-close]')?.addEventListener('click', () => this.closeOptions(), { signal });
      this.querySelector('[data-pse-change]')?.addEventListener('click', () => this.openFlow(), { signal });

      this.pop?.addEventListener('click', (event) => {
        if (event.target === this.pop) this.closeOptions();
        if (event.target.closest('[data-action="change-destination"]')) {
          this.closeOptions();
          this.openDestinationPopup();
        }
      }, { signal });

      this.listEl?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-delivery-select]');
        if (btn) this.selectScenario(btn.dataset.deliverySelect);
      }, { signal });

      this.initDestinationPopup(signal);

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.isDestinationOpen()) {
          this.closeDestinationPopup();
          this.trigger?.focus();
          return;
        }
        if (this.isOptionsOpen()) {
          this.closeOptions();
          const target = this.classList.contains('is-picked')
            ? this.querySelector('[data-pse-change]')
            : this.trigger;
          target?.focus();
        }
      }, { signal });

      this.section = this.closest('[data-section]');
      this.section?.addEventListener('on:variant:change', (event) => {
        if (event.detail?.variant?.id) {
          this.currentVariantId = event.detail.variant.id;
          this.onQuoteInputsChanged();
        }
      }, { signal });

      this.qtyInput = this.closest('.product-info')?.querySelector('.js-product-form-main [name="quantity"]');
      if (this.qtyInput) {
        ['change', 'input'].forEach((type) => {
          this.qtyInput.addEventListener(type, () => this.onQuoteInputsChanged(), { signal });
        });
      }

      this.populateCountrySelect();
      this.restorePersistedState();
      this.updateShipToDisplay();
      this.renderPicked();
      this.updateContext();

      // Quietly hydrate a previously picked option — no popup on page load.
      if (this.destination?.country && this.scenarioId) {
        this.scheduleFetch();
      }
    }

    disconnectedCallback() {
      if (this.isOptionsOpen() || this.isDestinationOpen()) {
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
      this.abortController?.abort();
      this.fetchAbort?.abort();
      if (this.fetchTimer) clearTimeout(this.fetchTimer);
    }

    // ---- open flow ----

    openFlow() {
      if (!this.destination?.country) {
        this.openDestinationPopup();
        return;
      }
      this.openOptions();
    }

    onQuoteInputsChanged() {
      this.lastFetchedKey = '';
      this.scenarios = [];
      this.updateContext();
      this.renderPicked();
      if (this.isOptionsOpen() && this.destination?.country) {
        this.scheduleFetch();
      }
    }

    // ---- destination popup ----

    initDestinationPopup(signal) {
      const overlay = this.destinationOverlay;
      if (!overlay) return;

      overlay.addEventListener('click', (event) => {
        if (event.target.closest('[data-destination-confirm]')) {
          this.confirmDestination();
          return;
        }
        if (event.target.closest('[data-destination-close]')) {
          this.closeDestinationPopup();
        }
      }, { signal });

      const select = overlay.querySelector('[data-destination-country]');
      const confirmBtn = overlay.querySelector('[data-destination-confirm]');
      select?.addEventListener('change', () => {
        if (confirmBtn) confirmBtn.disabled = !select.value;
        this.updateDetectHint('', { suggested: false });
      }, { signal });

      select?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && select.value) {
          event.preventDefault();
          this.confirmDestination();
        }
      }, { signal });
    }

    isDestinationOpen() {
      return this.destinationOverlay && !this.destinationOverlay.hidden;
    }

    openDestinationPopup() {
      const overlay = this.destinationOverlay;
      if (!overlay) return;

      this.closeOptions({ keepBodyLock: true });

      const select = overlay.querySelector('[data-destination-country]');
      const confirmBtn = overlay.querySelector('[data-destination-confirm]');
      const saved = this.destination?.country || '';
      const suggested = !saved ? this.resolveSuggestedCountry() : '';
      const selected = saved || suggested;

      if (select) select.value = selected;
      if (confirmBtn) confirmBtn.disabled = !select?.value;
      this.updateDetectHint(suggested, { suggested: Boolean(suggested) });

      overlay.hidden = false;
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        const focusTarget = confirmBtn && !confirmBtn.disabled ? confirmBtn : select;
        if (typeof trapFocus === 'function') {
          trapFocus(overlay, focusTarget);
        } else {
          focusTarget?.focus({ preventScroll: true });
        }
      });
    }

    closeDestinationPopup() {
      const overlay = this.destinationOverlay;
      if (!overlay || overlay.hidden) return;

      overlay.classList.remove('is-open');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        overlay.removeEventListener('transitionend', onEnd);
        overlay.hidden = true;
        if (!this.isOptionsOpen()) {
          document.body.classList.remove('overflow-hidden');
          if (typeof removeTrapFocus === 'function') removeTrapFocus();
        }
      };
      const onEnd = (event) => {
        if (event.target !== overlay) return;
        finish();
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      overlay.addEventListener('transitionend', onEnd);
      setTimeout(finish, 350);
    }

    confirmDestination() {
      const select = this.destinationOverlay?.querySelector('[data-destination-country]');
      const country = select?.value || '';
      if (!country) {
        select?.focus();
        return;
      }

      const changed = country !== this.destination?.country;
      this.destination = { country, zip: this.destination?.zip || '' };
      try {
        localStorage.setItem(DESTINATION_STORAGE_KEY, JSON.stringify(this.destination));
      } catch {
        /* storage unavailable */
      }

      if (changed) {
        this.scenarioId = null;
        this.scenarios = [];
        this.lastFetchedKey = '';
        this.persistState();
      }

      this.updateShipToDisplay();
      this.closeDestinationPopup();
      this.openOptions();
    }

    readStoredDestination() {
      try {
        const saved = JSON.parse(localStorage.getItem(DESTINATION_STORAGE_KEY));
        return saved?.country ? saved : null;
      } catch {
        return null;
      }
    }

    populateCountrySelect() {
      const select = this.destinationOverlay?.querySelector('[data-destination-country]');
      if (!select || select.dataset.populated === 'true') return;

      const rest = COUNTRY_CODES
        .filter((code) => !POPULAR_COUNTRIES.includes(code))
        .map((code) => ({ code, name: this.countryName(code) }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const popularGroup = document.createElement('optgroup');
      popularGroup.label = 'Popular';
      POPULAR_COUNTRIES.forEach((code) => popularGroup.append(new Option(this.countryName(code), code)));

      const allGroup = document.createElement('optgroup');
      allGroup.label = 'All countries';
      rest.forEach(({ code, name }) => allGroup.append(new Option(name, code)));

      select.append(popularGroup, allGroup);
      select.dataset.populated = 'true';
    }

    resolveSuggestedCountry() {
      if (!this.autodetectCountry) return '';

      if (this.detectedCountry && COUNTRY_CODES.includes(this.detectedCountry)) {
        return this.detectedCountry;
      }

      try {
        const locale = String(navigator.language || '').trim();
        const region = locale.includes('-') ? locale.split('-').pop().toUpperCase() : '';
        if (region.length === 2 && COUNTRY_CODES.includes(region)) return region;
      } catch {
        /* ignore */
      }
      return '';
    }

    updateDetectHint(code, { suggested = false } = {}) {
      const hint = this.destinationOverlay?.querySelector('[data-destination-detect]');
      if (!hint) return;

      if (!suggested || !code) {
        hint.hidden = true;
        hint.textContent = '';
        return;
      }

      const template = this.i18n.detectedCountryHint || 'Suggested from your location: __COUNTRY__';
      hint.textContent = template
        .replaceAll('__COUNTRY__', this.countryName(code))
        .replaceAll('{country}', this.countryName(code));
      hint.hidden = false;
    }

    countryName(code) {
      if (!code) return '';
      try {
        this.regionNames ||= new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
        return this.regionNames.of(code) || code;
      } catch {
        return code;
      }
    }

    updateShipToDisplay() {
      if (!this.shipToLabelEl) return;
      const { country, zip } = this.destination || {};
      const place = country ? this.countryName(country) + (zip ? `, ${zip}` : '') : '-';
      const template = this.i18n.shipTo || 'Ship to __COUNTRY__';
      this.shipToLabelEl.textContent = template
        .replaceAll('__COUNTRY__', place)
        .replaceAll('{country}', place);
    }

    // ---- options modal ----

    isOptionsOpen() {
      return this.pop && !this.pop.hidden;
    }

    openOptions() {
      if (!this.pop) return;

      this.updateContext();
      this.updateShipToDisplay();

      // Paint skeleton before paint of the open dialog when rates aren't ready.
      if (!this.scenarios.length && this.destination?.country) {
        this.isLoading = true;
        this.renderOptions();
      }

      this.pop.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');

      const focusTarget = this.pop.querySelector('[data-pse-dialog]');
      if (typeof trapFocus === 'function') {
        trapFocus(this.pop, focusTarget);
      } else {
        focusTarget?.focus();
      }

      this.scheduleFetch();
    }

    closeOptions({ keepBodyLock = false } = {}) {
      if (!this.pop || this.pop.hidden) return;
      this.pop.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!keepBodyLock && !this.isDestinationOpen()) {
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
    }

    // ---- API ----

    getQuantity() {
      const qty = Number.parseInt(this.qtyInput?.value, 10);
      return Number.isNaN(qty) || qty < 1 ? 1 : qty;
    }

    getCurrentVariant() {
      return this.variants.find((variant) => variant.id === this.currentVariantId) || this.variants[0];
    }

    buildRequestBody() {
      const variant = this.getCurrentVariant();
      if (!variant || !this.destination?.country) return null;

      return {
        items: [{
          variant_id: `gid://shopify/ProductVariant/${variant.id}`,
          qty: this.getQuantity()
        }],
        destination: {
          country: this.destination.country,
          province: '',
          city: '',
          postal_code: this.destination.zip || '',
          address1: '',
          address2: '',
          name: '',
          company_name: ''
        }
      };
    }

    scheduleFetch() {
      if (this.fetchTimer) clearTimeout(this.fetchTimer);

      if (!this.scenarios.length && this.destination?.country) {
        this.isLoading = true;
        this.renderOptions();
        this.renderPicked();
      }

      this.fetchTimer = setTimeout(() => this.fetchScenarios(), FETCH_DEBOUNCE_MS);
    }

    async fetchScenarios() {
      const body = this.buildRequestBody();
      if (!this.apiUrl || !body) return;

      const key = JSON.stringify(body);
      if (key === this.lastFetchedKey && !this.hasError) {
        this.isLoading = false;
        this.renderStatus();
        this.renderOptions();
        return;
      }

      this.fetchAbort?.abort();
      this.fetchAbort = new AbortController();
      this.isLoading = true;
      this.hasError = false;
      this.renderOptions();

      try {
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: key,
          signal: this.fetchAbort.signal
        });
        if (!response.ok) throw new Error(`Estimate request failed: ${response.status}`);

        const data = await response.json();
        this.lastFetchedKey = key;
        this.scenarios = this.filterScenarios(Array.isArray(data.scenarios) ? data.scenarios : []);
        this.isLoading = false;
        this.validateSelection();
        this.renderOptions();
        this.renderPicked();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Product shipping estimate error:', error);
        this.isLoading = false;
        this.hasError = true;
        this.renderOptions();
      }
    }

    filterScenarios(scenarios) {
      const presented = scenarios.filter((s) => PRESENTED_CODES.includes(s.code));
      if (this.destination?.country === 'TR') {
        return presented.filter((s) => s.code === 'ISTANBUL_PICKUP');
      }
      return presented;
    }

    validateSelection() {
      const stillAvailable = this.scenarios.some((s) => s.code === this.scenarioId);
      if (!stillAvailable) this.scenarioId = null;

      if (!this.scenarioId && this.scenarios.length === 1) {
        this.scenarioId = this.scenarios[0].code;
      }
      this.persistState();
    }

    // ---- selection / persistence ----

    selectScenario(code) {
      if (!this.scenarios.some((s) => s.code === code)) return;
      this.scenarioId = code;
      this.persistState();
      this.closeOptions();
      this.renderPicked();
      this.querySelector('[data-pse-change]')?.focus();
    }

    persistState() {
      try {
        sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify({
          scenarioId: this.scenarioId,
          country: this.destination?.country || null
        }));
      } catch {
        /* storage unavailable */
      }
    }

    restorePersistedState() {
      const saved = this.parseJson(sessionStorage.getItem(STATE_STORAGE_KEY), null);
      if (saved?.scenarioId && saved.country === this.destination?.country) {
        this.scenarioId = saved.scenarioId;
      }
    }

    // ---- rendering ----

    updateContext() {
      if (!this.ctxEl) return;
      this.ctxEl.textContent = (this.i18n.context || '')
        .replace('__QTY__', this.getQuantity())
        .replace('__KG__', this.formatWeightKg());
    }

    formatWeightKg() {
      const variant = this.getCurrentVariant();
      if (!variant) return '0';
      const kg = (variant.weightG * this.getQuantity()) / 1000;
      return (Math.round(kg * 100) / 100).toString();
    }

    scenarioTitle(scenario) {
      return (scenario.name || '').trim() || FALLBACK_NAMES[scenario.code] || scenario.code;
    }

    scenarioSubtitle(scenario) {
      const leadTime = (scenario.lead_time || '').trim();
      if (!leadTime) return this.i18n.deliveryVaries || 'Estimated delivery: varies';
      const template = this.i18n.deliveryEstimate || 'Estimated delivery: __LEAD_TIME__';
      return template.replace('__LEAD_TIME__', leadTime);
    }

    getFeesForScenario(scenario) {
      const variant = this.getCurrentVariant();
      if (!variant || !scenario) return null;
      const feesCents = this.toCents(scenario.total);
      const subtotalCents = variant.price * this.getQuantity();
      return {
        feesCents,
        totalCents: subtotalCents + feesCents,
        leadTime: scenario.lead_time || ''
      };
    }

    renderSkeletonRows(count = 3) {
      return Array.from({ length: count }, () => `
        <div class="pse__row pse__row--skeleton" aria-hidden="true">
          <span class="pse__row-radio"></span>
          <span class="pse__row-main">
            <span class="pse__bone pse__bone--title"></span>
            <span class="pse__bone pse__bone--sub"></span>
          </span>
          <span class="pse__row-right">
            <span class="pse__bone pse__bone--price"></span>
            <span class="pse__bone pse__bone--meta"></span>
          </span>
        </div>
      `).join('');
    }

    renderOptions() {
      if (!this.listEl) return;

      const showSkeleton = this.isLoading && !this.scenarios.length;
      this.listEl.classList.toggle('is-loading', this.isLoading && this.scenarios.length > 0);
      this.listEl.classList.toggle('is-skeleton', showSkeleton);
      this.listEl.setAttribute('aria-busy', showSkeleton || this.isLoading ? 'true' : 'false');

      if (showSkeleton) {
        this.listEl.innerHTML = this.renderSkeletonRows(3);
        this.renderStatus();
        return;
      }

      this.listEl.innerHTML = this.scenarios.map((scenario) => {
        const fees = this.getFeesForScenario(scenario);
        const isSelected = scenario.code === this.scenarioId;
        const delta = fees.feesCents === 0
          ? (this.i18n.free || 'Free')
          : `+${this.formatMoney(fees.feesCents)}`;
        const total = (this.i18n.totalSuffix || '= __PRICE__')
          .replace('__PRICE__', this.formatMoney(fees.totalCents));

        return `
          <button
            type="button"
            class="pse__row${isSelected ? ' is-selected' : ''}"
            data-delivery-select="${this.escapeHtml(scenario.code)}"
            role="radio"
            aria-checked="${isSelected ? 'true' : 'false'}"
          >
            <span class="pse__row-radio" aria-hidden="true">
              <span class="pse__row-radio-dot"></span>
            </span>
            <span class="pse__row-main">
              <span class="pse__row-name">${this.escapeHtml(this.scenarioTitle(scenario))}</span>
              <span class="pse__row-lead">${this.escapeHtml(this.scenarioSubtitle(scenario))}</span>
            </span>
            <span class="pse__row-right">
              <span class="pse__row-delta">${this.escapeHtml(delta)}</span>
              <span class="pse__row-total">${this.escapeHtml(total)}</span>
            </span>
          </button>
        `;
      }).join('');

      this.renderStatus();
    }

    renderStatus() {
      if (!this.statusEl) return;

      let message = '';
      // Skeleton covers the initial loading state — keep status for error/empty only.
      if (this.hasError) {
        message = this.i18n.error || 'We could not load shipping rates. Please try again.';
      } else if (!this.isLoading && !this.scenarios.length && this.destination?.country && this.lastFetchedKey) {
        message = this.i18n.empty || 'No shipping options are available for this destination yet.';
      }

      this.statusEl.textContent = message;
      this.statusEl.hidden = !message;
    }

    renderPicked() {
      if (!this.picked) return;

      const scenario = this.scenarios.find((s) => s.code === this.scenarioId);
      const fees = scenario ? this.getFeesForScenario(scenario) : null;

      if (!this.scenarioId) {
        this.picked.hidden = true;
        this.picked.classList.remove('is-skeleton');
        this.classList.remove('is-picked');
        return;
      }

      this.picked.hidden = false;
      this.classList.add('is-picked');

      if (!fees) {
        this.picked.classList.add('is-skeleton');
        this.setText('[data-pse-picked-name]', FALLBACK_NAMES[this.scenarioId] || this.scenarioId);
        this.setHtml(
          '[data-pse-picked-lead]',
          '<span class="pse__bone pse__bone--sub" aria-hidden="true"></span>'
        );
        this.setHtml(
          '[data-pse-picked-total]',
          '<span class="pse__bone pse__bone--price" aria-hidden="true"></span>'
        );
        this.setHtml(
          '[data-pse-picked-delta]',
          '<span class="pse__bone pse__bone--meta" aria-hidden="true"></span>'
        );
        return;
      }

      this.picked.classList.remove('is-skeleton');
      this.setText('[data-pse-picked-name]', this.scenarioTitle(scenario));
      this.setText('[data-pse-picked-lead]', this.scenarioSubtitle(scenario));
      this.setText(
        '[data-pse-picked-total]',
        (this.i18n.delivered || '__PRICE__').replace('__PRICE__', this.formatMoney(fees.totalCents))
      );
      this.setText(
        '[data-pse-picked-delta]',
        fees.feesCents === 0
          ? (this.i18n.noFees || '')
          : (this.i18n.deltaFees || '+__PRICE__').replace('__PRICE__', this.formatMoney(fees.feesCents))
      );
    }

    setText(selector, value) {
      const el = this.querySelector(selector);
      if (el) el.textContent = value;
    }

    setHtml(selector, html) {
      const el = this.querySelector(selector);
      if (el) el.innerHTML = html;
    }

    // ---- helpers ----

    parseJson(value, fallback) {
      if (!value) return fallback;
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }

    toCents(decimalString) {
      const amount = Number.parseFloat(decimalString);
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      if (this.moneyFormat.includes('{{amount_with_comma_separator}}')) {
        return this.moneyFormat.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
      }
      if (this.moneyFormat.includes('{{amount}}')) {
        return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
      }
      return `$${amount}`;
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }
  }

  customElements.define('product-shipping-estimate', ProductShippingEstimate);
}
